"use client";
import React, { forwardRef } from "react";

/**
 * Sukuto unified Button.
 *
 * One component to replace the ~8 ad-hoc primary-button shapes that
 * existed before. All variants share the same height/padding/radius
 * scale so a row of mixed-variant buttons aligns by default.
 *
 *   <Button>Search</Button>                          // primary (coral pill)
 *   <Button variant="dark">Save</Button>             // user-account flow
 *   <Button variant="ghost">Cancel</Button>
 *   <Button variant="ghost-red">Reset</Button>
 *   <Button variant="link">Show more</Button>
 *   <Button variant="danger">Delete</Button>
 *   <Button variant="whatsapp">Chat</Button>
 *
 *   <Button size="sm">…</Button>                     // 36px
 *   <Button size="md">…</Button>                     // 44px (default)
 *   <Button size="lg">…</Button>                     // 48px
 *   <Button size="xl">…</Button>                     // 55px (full-bleed hero)
 *
 *   <Button block>…</Button>                         // w-full
 *   <Button loading>…</Button>                       // shows spinner
 *   <Button leftIcon={<Icon/>} rightIcon={<ChevronRight/>}>Book now</Button>
 *
 * Renders <a> instead of <button> when `href` is provided.
 */

const BASE =
  "inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap " +
  "transition-[background-color,box-shadow,transform,color,border-color] duration-200 " +
  "outline-none cursor-pointer select-none " +
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none " +
  "focus-visible:outline-none focus-visible:shadow-ring";

const VARIANT = {
  primary:
    "bg-primary text-white border border-transparent " +
    "hover:bg-primary-dark hover:shadow-cta hover:scale-[1.02] " +
    "active:bg-primary-deep active:scale-[1]",

  // For the user/account flow — the existing "dark CTA" pattern.
  dark:
    "bg-heading text-white border border-transparent " +
    "hover:bg-headingHover active:bg-black",

  // Outlined neutral — "View details" / "Cancel"
  ghost:
    "bg-white text-textPrimary border border-lineStrong " +
    "hover:border-primary hover:text-primary-deep",

  // Outlined red — filter chip "active state" / "Reset"
  "ghost-red":
    "bg-redSurface-50 text-primary-deep border border-redSurface-200 " +
    "hover:bg-redSurface-100 hover:border-primary",

  // Inline text link — replaces the half-dozen <button class="text-primary underline"> sites
  link:
    "bg-transparent text-primary underline underline-offset-2 px-0 h-auto " +
    "hover:text-primary-dark hover:decoration-2 " +
    "focus-visible:shadow-ring-soft rounded-sm",

  // System destructive
  danger:
    "bg-danger text-white border border-transparent " +
    "hover:bg-dangerDeep active:bg-dangerDeep",

  // WhatsApp brand
  whatsapp:
    "bg-whatsapp text-white border border-transparent " +
    "hover:brightness-95 active:brightness-90",
};

const SIZE = {
  sm: "h-9 px-4 text-[13px] rounded-pill",
  md: "h-11 px-5 text-[14px] rounded-pill",
  lg: "h-12 px-6 text-[15px] rounded-pill",
  xl: "h-[55px] px-8 text-[16px] rounded-pill",
};

// `link` variant ignores SIZE
const SIZE_FOR_LINK = "text-[14px]";

const Button = forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    block = false,
    loading = false,
    leftIcon,
    rightIcon,
    href,
    type = "button",
    className = "",
    children,
    disabled,
    ...rest
  },
  ref
) {
  const sizeClasses = variant === "link" ? SIZE_FOR_LINK : SIZE[size];
  const cls = [
    BASE,
    VARIANT[variant] || VARIANT.primary,
    sizeClasses,
    block ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {loading ? (
        <Spinner />
      ) : (
        leftIcon && <span className="inline-flex shrink-0 [&_svg]:w-4 [&_svg]:h-4">{leftIcon}</span>
      )}
      <span>{children}</span>
      {!loading && rightIcon && (
        <span className="inline-flex shrink-0 [&_svg]:w-4 [&_svg]:h-4">{rightIcon}</span>
      )}
    </>
  );

  if (href) {
    return (
      <a ref={ref} href={href} className={cls} aria-disabled={disabled || loading} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      type={type}
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
});

function Spinner() {
  return (
    <svg
      className="animate-spin shrink-0 w-4 h-4"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default Button;
